import HiddenInput from "@/components/form/components/HiddenInput";
import AntIcon from "@/components/icons/AntIcon";
import HtmlView from "@/components/views/HtmlView";
import { quillEditorStandardProps, quillFormItemProps } from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Button, Card, Empty, Flex, Form, Modal } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill-new";
import { useUpdateContractComplaintNote } from "../../../../common/api/queries/";
import { ModalSizes } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { UUID } from "../../../../typings/global";
import ComponentWithPermission from "../../../auth/components/ComponentWithPermission";
import { Contract, UpdateContractComplaintNote } from "../../types";

type Props = {
  data: {
    id: UUID;
    complaintNote?: Contract["complaintNote"];
    optimisticLockVersion?: Contract["optimisticLockVersion"];
  };
  updatePermission: Permission;
};

export const ContractComplaints = ({ data: { complaintNote, optimisticLockVersion, id }, updatePermission }: Props) => {
  const { t } = useTranslation();

  const [isFormOpen, setIsFormOpen] = useState(false);

  const hasComplaintNote = complaintNote && complaintNote !== "<p></p>";

  const [form] = Form.useForm<UpdateContractComplaintNote>();

  const updateComplaintNote = useUpdateContractComplaintNote();

  useEffect(() => {
    if (isFormOpen) {
      form.setFieldsValue({ complaintNote, optimisticLockVersion });
    }
  }, [complaintNote, isFormOpen, optimisticLockVersion]);

  return (
    <>
      <div>
        <Flex justify="space-between" align="end">
          <span>{t("contract.attrs.complaintNote")}</span>

          <ComponentWithPermission permissions={[updatePermission]}>
            <Button
              color="primary"
              variant="solid"
              icon={hasComplaintNote ? <AntIcon type="edit" /> : <AntIcon type="plus" />}
              onClick={() => setIsFormOpen(true)}
            >
              {hasComplaintNote ? t("contract.actions.updateComplainNote") : t("contract.actions.createComplainNote")}
            </Button>
          </ComponentWithPermission>
        </Flex>

        <Card className="margin-top-tiny">{hasComplaintNote ? <HtmlView value={complaintNote} /> : <Empty />}</Card>
      </div>

      <Modal
        width={ModalSizes.MEDIUM}
        open={isFormOpen}
        afterClose={() => form.resetFields()}
        onCancel={() => setIsFormOpen(false)}
        onOk={() =>
          form.validateFields().then((values) =>
            updateComplaintNote.mutate(
              { id, object: values },
              {
                onSuccess: () => setIsFormOpen(false),
                onError: (error) => setErrorsToForm(form, "contract.attrs", error.violations),
              },
            ),
          )
        }
        title={t("contract.attrs.complaintNote")}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        confirmLoading={updateComplaintNote.isPending}
        mask={{ closable: false }}
      >
        <Form form={form} layout="vertical" name="contractComplainsForm" className="margin-top-medium">
          <HiddenInput name="optimisticLockVersion" initialValue={optimisticLockVersion} />

          <Form.Item
            name="complaintNote"
            rules={[validations.size(1, 16384)]}
            {...quillFormItemProps}
            initialValue={complaintNote}
          >
            <ReactQuill {...quillEditorStandardProps} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
